import { Badge, Button, Input } from "@simplicity/ui";
import { X } from "lucide-react";
import { useState } from "react";

interface EntityIdsInputProps {
  id?: string;
  /** Current list of entity IDs (numbers). */
  value: number[];
  onChange: (next: number[]) => void;
  placeholder?: string;
}

/**
 * Self-contained multi-value chip input for a numeric `number[]` field (Skyward `vars.entityIds`).
 * Deliberately not shared with the PowerSchool school-ids input: the stored value here is
 * `number[]` and the UI carries no school-id terminology. Comma or Enter commits (non-numeric
 * ignored), duplicates drop with order preserved, Backspace removes the last chip.
 */
export function EntityIdsInput(props: EntityIdsInputProps) {
  const { value, onChange, id, placeholder } = props;
  const [draft, setDraft] = useState("");

  function commit(raw: string) {
    const parsed = raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n));
    if (parsed.length === 0) {
      setDraft("");
      return;
    }
    const seen = new Set<number>();
    const unique = [...value, ...parsed].filter((n) => (seen.has(n) ? false : (seen.add(n), true)));
    onChange(unique);
    setDraft("");
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  }

  function handleBlur() {
    if (draft.trim()) commit(draft);
  }

  function removeAt(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  return (
    <div data-testid="entity-ids-input">
      <div className="flex flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1.5">
        {value.map((eid, idx) => (
          <Badge key={`${eid}-${idx}`} variant="secondary" className="font-mono text-xs">
            {eid}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-1 h-auto p-0.5"
              onClick={() => removeAt(idx)}
              aria-label={`Remove ${eid}`}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
        <Input
          id={id}
          type="text"
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          onBlur={handleBlur}
          placeholder={
            placeholder ?? (value.length === 0 ? "Add entity IDs (Enter or comma)…" : "")
          }
          className="h-7 min-w-[12ch] flex-1 border-0 bg-transparent p-0 font-mono text-xs shadow-none focus-visible:ring-0"
        />
      </div>
    </div>
  );
}
